import type { ReactNode } from 'react';
import { Suspense, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { FileServerProvider } from '@repo/ui/file-server-context';
import {
  NavigationProgressBar,
  NavigationProgressProvider,
} from '@repo/ui/navigation-progress';
import {
  RouterAdapterProvider,
  type RouterAdapter,
} from '@repo/ui/router-adapter';

import { CriticalErrorBoundary } from '@/components/critical-error-boundary';
import { ProcessingBanner } from '@/components/processing-banner';
import { ReduxProvider } from '@/components/redux-provider';
import { ScopeHeadersProvider } from '@/components/scope-headers-provider';
import { CurrentTenantProvider } from '@/contexts/current-tenant-context';
import { CurrentUserProvider } from '@/contexts/current-user-context';
import { ProcessingLockProvider } from '@/contexts/processing-lock-context';
import { TimeFormatProvider } from '@/contexts/time-format-context';
import { AuthProvider } from '@/lib/auth';

interface ProvidersProps {
  children: ReactNode;
}

function RouterAdapterBridge({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const adapter: RouterAdapter = useMemo(
    () => ({
      pathname: location.pathname,
      searchParams,
      push: (url: string) => navigate(url),
      replace: (url: string) => navigate(url, { replace: true }),
      back: () => navigate(-1),
    }),
    [location.pathname, searchParams, navigate],
  );

  return (
    <RouterAdapterProvider adapter={adapter}>{children}</RouterAdapterProvider>
  );
}

/**
 * Client-side providers wrapper
 *
 * Provider hierarchy:
 * 1. AuthProvider - Manages Zitadel authentication state
 * 2. ReduxProvider - Redux store for RTK Query
 * 3. RouterAdapterBridge - Bridges React Router to @repo/ui router context
 * 4. NavigationProgressProvider - Navigation loading indicators
 * 5. CurrentTenantProvider - Tenant context
 * 6. CurrentUserProvider - User context
 * 7. ProcessingLockProvider - SSE-based processing lock state + cache invalidation
 * 8. CriticalErrorBoundary - Shows error page if tenant/user APIs fail
 */
export function Providers({ children }: ProvidersProps) {
  return (
    <Suspense fallback={null}>
      <AuthProvider>
        <ReduxProvider>
          <RouterAdapterBridge>
            <ScopeHeadersProvider>
              <NavigationProgressProvider>
                <NavigationProgressBar />
                <FileServerProvider
                  url={import.meta.env.VITE_FILE_SERVER_URL ?? ''}
                >
                  <CurrentTenantProvider>
                    <CurrentUserProvider>
                      <TimeFormatProvider>
                        <ProcessingLockProvider>
                          <ProcessingBanner />
                          <CriticalErrorBoundary>
                            {children}
                          </CriticalErrorBoundary>
                        </ProcessingLockProvider>
                      </TimeFormatProvider>
                    </CurrentUserProvider>
                  </CurrentTenantProvider>
                </FileServerProvider>
              </NavigationProgressProvider>
            </ScopeHeadersProvider>
          </RouterAdapterBridge>
        </ReduxProvider>
      </AuthProvider>
    </Suspense>
  );
}
